import { useCallback } from 'react'

import { useUpdateUserSetting } from './useUpdateUserSetting'

type ColumnType = 'dashboard' | 'customer'

interface ColumnConfig {
  userSettingKey: 'dashboardColumns' | 'customerColumns'
  successMessage: string
  errorMessage: string
}

const COLUMN_CONFIGS: Record<ColumnType, ColumnConfig> = {
  dashboard: {
    userSettingKey: 'dashboardColumns',
    successMessage: 'Spalten-Einstellungen gespeichert.',
    errorMessage: 'Fehler beim Speichern der Spalten-Einstellungen.',
  },
  customer: {
    userSettingKey: 'customerColumns',
    successMessage: 'Spalten-Einstellungen gespeichert.',
    errorMessage: 'Fehler beim Speichern der Kunden-Spalten-Einstellungen.',
  },
}

/**
 * Generic hook for updating column visibility settings.
 * Stores preferences per-user in user.settings instead of tenant-wide.
 * This allows each user to have their own column visibility preferences.
 *
 * @param columnType - Type of columns to update ('dashboard' or 'customer')
 * @returns Object with updateColumnVisibility function and isUpdating state
 *
 * @example
 * ```tsx
 * const { updateColumnVisibility, isUpdating } = useUpdateColumnVisibility('dashboard')
 * updateColumnVisibility(['name', 'email', 'phone'])
 * ```
 */
export const useUpdateColumnVisibility = (columnType: ColumnType) => {
  const config = COLUMN_CONFIGS[columnType]
  const { update, isUpdating } = useUpdateUserSetting(
    config.userSettingKey,
    config.errorMessage,
    config.successMessage
  )

  const updateColumnVisibility = useCallback(
    (visibleColumns: string[]) => {
      update(visibleColumns)
    },
    [update]
  )

  return { updateColumnVisibility, isUpdating }
}
